// @ts-nocheck
import { useEffect, useMemo, useState } from "react";
import {
  fetchBrokerCapabilities,
  type BrokerCapabilities,
  type TradeAccountAnalytics,
} from "../services/brokerIntegrationApi";
import { useActiveBrokerAccount } from "./useActiveBrokerAccount";
import {
  findTradeAccount,
  getTradeAccountAnalytics,
  supportsPortfolioBalanceFromBrokerAnalytics,
  supportsPortfolioGrowthFromAnalytics,
} from "../utils/tradeAccountAnalytics";

type ActiveBrokerCapabilitiesResult = {
  /** Broker capability row for the active broker (auth, CSV endpoints, csv_import_analytics). */
  capabilities: BrokerCapabilities | null;
  /** Resolved analytics for the active trade account (GET /broker/trade-accounts). */
  accountAnalytics: TradeAccountAnalytics | null;
  loading: boolean;
  error: string | null;
  supportsPortfolioGrowth: boolean;
  supportsPortfolioBalanceFromBroker: boolean;
  defaultGrowthBasis: string | null;
};

const normalizeBrokerName = (brokerName: string): string => {
  if (brokerName === "liquid") return "liquidbrokers";
  return brokerName;
};

let brokerCapabilitiesCache: BrokerCapabilities[] | null = null;
let brokerCapabilitiesInflight: Promise<BrokerCapabilities[]> | null = null;

async function loadBrokerCapabilitiesCached(): Promise<BrokerCapabilities[]> {
  if (brokerCapabilitiesCache) return brokerCapabilitiesCache;
  if (brokerCapabilitiesInflight) return brokerCapabilitiesInflight;

  brokerCapabilitiesInflight = fetchBrokerCapabilities()
    .then((rows) => {
      brokerCapabilitiesCache = rows;
      return rows;
    })
    .finally(() => {
      brokerCapabilitiesInflight = null;
    });

  return brokerCapabilitiesInflight;
}

/** Clear after broker connect/disconnect so sidebar resync visibility stays accurate. */
export function invalidateBrokerCapabilitiesCache(): void {
  brokerCapabilitiesCache = null;
  brokerCapabilitiesInflight = null;
}

type ActiveBrokerCapabilitiesOptions = {
  /** When true, skip GET /broker/capabilities (dashboard only needs trade-account analytics). */
  skipCapabilitiesFetch?: boolean;
};

/**
 * Dashboard / Statistics feature flags come from the active account's `analytics` on
 * GET /broker/trade-accounts — not from broker-level capabilities or `source`.
 */
export function useActiveBrokerCapabilities(
  activeAccountCode?: string | null,
  activeBrokerName?: string | null,
  options?: ActiveBrokerCapabilitiesOptions
): ActiveBrokerCapabilitiesResult {
  const skipCapabilitiesFetch = options?.skipCapabilitiesFetch === true;
  const { accounts, loading: accountsLoading } = useActiveBrokerAccount();
  const [capabilities, setCapabilities] = useState<BrokerCapabilities | null>(null);
  const [capabilitiesLoading, setCapabilitiesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (skipCapabilitiesFetch) {
      setCapabilities(null);
      setCapabilitiesLoading(false);
      setError(null);
      return;
    }
    let isCancelled = false;
    const slug = activeBrokerName ? normalizeBrokerName(activeBrokerName).toLowerCase() : null;

    const load = async () => {
      if (!slug) {
        if (!isCancelled) setCapabilities(null);
        return;
      }
      setCapabilitiesLoading(true);
      setError(null);
      try {
        const all = await loadBrokerCapabilitiesCached();
        const row =
          (Array.isArray(all) ? all : []).find(
            (c) => normalizeBrokerName(c.broker_name).toLowerCase() === slug
          ) ?? null;
        if (!isCancelled) setCapabilities(row);
      } catch (err: any) {
        if (!isCancelled) {
          setError(err?.message || "Failed to load broker capabilities");
          setCapabilities(null);
        }
      } finally {
        if (!isCancelled) setCapabilitiesLoading(false);
      }
    };

    void load();
    return () => {
      isCancelled = true;
    };
  }, [activeBrokerName, skipCapabilitiesFetch]);

  const activeAccount = useMemo(
    () => findTradeAccount(accounts, activeAccountCode, activeBrokerName),
    [accounts, activeAccountCode, activeBrokerName]
  );

  const accountAnalytics = useMemo(
    () => getTradeAccountAnalytics(activeAccount),
    [activeAccount]
  );

  const supportsPortfolioGrowth = useMemo(
    () => supportsPortfolioGrowthFromAnalytics(accountAnalytics, accountsLoading),
    [accountAnalytics, accountsLoading]
  );

  const supportsPortfolioBalanceFromBroker = useMemo(
    () => supportsPortfolioBalanceFromBrokerAnalytics(accountAnalytics),
    [accountAnalytics]
  );

  const defaultGrowthBasis = accountAnalytics?.default_growth_basis ?? null;

  return {
    capabilities,
    accountAnalytics,
    loading: accountsLoading || (skipCapabilitiesFetch ? false : capabilitiesLoading),
    error,
    supportsPortfolioGrowth,
    supportsPortfolioBalanceFromBroker,
    defaultGrowthBasis,
  };
}
